import type { AxiosInstance } from 'axios';
import type {
  CreateMessageGenSessionRequest,
  CreateGeneratedMessageRequest,
  UpdateGeneratedMessageRequest,
  MessageGenSession,
  MessageGenSessionSummary,
  MessageGenChatRequest,
  MarketingMessage,
  GeneratedMessage,
  MessageGenState,
} from '../types/message-generation.js';

export class MessageGenerationClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  createSession = async (data: CreateMessageGenSessionRequest): Promise<MessageGenSession> => {
    const res = await this.authenticatedClient.post<MessageGenSession>('/message-gen-sessions', data);
    return res.data;
  };

  getSession = async (messageGenSessionId: number): Promise<MessageGenSession> => {
    const res = await this.authenticatedClient.get<MessageGenSession>(`/message-gen-sessions/${messageGenSessionId}`);
    return res.data;
  };

  /** Send a user chat message to the LangGraph agent and receive the response. */
  chat = async (data: MessageGenChatRequest): Promise<{ responseMessage: string; state: MessageGenState }> => {
    const res = await this.authenticatedClient.post<{ responseMessage: string; state: MessageGenState }>(
      '/message-gen-sessions/chat',
      data,
    );
    return res.data;
  };

  startSession = async (): Promise<{ sessionId: string; chatInstanceId: string }> => {
    const res = await this.authenticatedClient.post<{ sessionId: string; chatInstanceId: string }>(
      '/message-gen-sessions/start',
    );
    return res.data;
  };

  listMessages = async (messageGenSessionId: number): Promise<MarketingMessage[]> => {
    const res = await this.authenticatedClient.get<MarketingMessage[]>(`/message-gen-sessions/${messageGenSessionId}/messages`);
    return res.data;
  };

  updateMessage = async (
    messageGenSessionId: number,
    messageIndex: number,
    updates: Partial<MarketingMessage>
  ): Promise<MarketingMessage> => {
    const res = await this.authenticatedClient.patch<MarketingMessage>(
      `/message-gen-sessions/${messageGenSessionId}/messages/${messageIndex}`,
      updates
    );
    return res.data;
  };

  saveMessages = async (messageGenSessionId: number): Promise<{ saved: number }> => {
    const res = await this.authenticatedClient.post<{ saved: number }>(
      `/message-gen-sessions/${messageGenSessionId}/messages/save`,
    );
    return res.data;
  };

  getAllMessages = async (): Promise<GeneratedMessage[]> => {
    const res = await this.authenticatedClient.get<GeneratedMessage[]>('/message-gen-sessions/messages/all');
    return res.data;
  };

  listSessions = async (limit: number = 10): Promise<MessageGenSessionSummary[]> => {
    const res = await this.authenticatedClient.get<MessageGenSessionSummary[]>('/message-gen-sessions', {
      params: { limit },
    });
    return res.data;
  };

  createMessage = async (data: CreateGeneratedMessageRequest): Promise<GeneratedMessage> => {
    const res = await this.authenticatedClient.post<GeneratedMessage>(
      '/message-gen-sessions/messages',
      data,
    );
    return res.data;
  };

  updateMessageById = async (
    messageId: number,
    updates: UpdateGeneratedMessageRequest
  ): Promise<GeneratedMessage> => {
    const res = await this.authenticatedClient.put<GeneratedMessage>(
      `/message-gen-sessions/messages/${messageId}`,
      updates,
    );
    return res.data;
  };
}
